import { createContext, useContext, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type SessionUser } from "./api";

interface SessionValue {
  user: SessionUser | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue>({
  user: null,
  loading: true,
  signOut: async () => {},
});

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["session"],
    queryFn: () => api.get<{ user: SessionUser | null }>("/auth/me"),
    retry: false,
    staleTime: 60_000,
  });

  const signOut = async () => {
    await api.post("/auth/logout");
    queryClient.clear();
    window.location.href = "/";
  };

  return (
    <SessionContext.Provider value={{ user: data?.user ?? null, loading: isLoading, signOut }}>
      {children}
    </SessionContext.Provider>
  );
}

export const useSession = () => useContext(SessionContext);
